import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type { useGetCollectionTableProps } from './config';
import type { InternalAuditReportResultRegisterFields } from './types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /** Whether the bulk-delete action should be offered at all. */
  canDelete: boolean;
  /** Greys the delete action (no rows selected). */
  deleteDisabled: boolean;
  onDelete: () => void;
}

/**
 * Object toolbar for the Internal Audit Report Results register, mounted only
 * when the `object_toolbar` flag is on. Owning `useExportOptions` here (rather
 * than on the page) keeps it off the legacy `ExportButton` path — with the flag
 * off this component never renders, so the export options are computed once, by
 * whichever path is actually shown (CUS-165).
 *
 * The bulk-delete gate is a build-time boolean (an OR across four result-type
 * `delete:*` permissions resolved on the page), so the danger action is included
 * conditionally rather than via the toolbar's per-action `permission` gate.
 */
export const InternalAuditReportResultsRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  canDelete,
  deleteDisabled,
  onDelete,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'assessmentResults',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<InternalAuditReportResultRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title, counter },
      actions: {
        ...(ribbonConfig
          ? {
              overflow: [
                {
                  id: 'edit',
                  label: t('edit'),
                  actions: [
                    {
                      id: 'edit-ribbon',
                      label: t('objectToolbar.filterSettings'),
                      iconName: 'filter-lines' as const,
                      onClick: ribbonConfig.openEditRibbon,
                    },
                  ],
                },
              ],
            }
          : {}),
        export: [
          {
            id: 'export',
            actions: exportOptions.map((option) => ({
              id: option.id,
              label: option.text,
              iconName: 'download-01' as const,
              onClick: option.exportFunction,
              disabled: option.disabled,
            })),
          },
        ],
        ...(canDelete
          ? {
              danger: [
                {
                  id: 'delete',
                  label: st('delete_button'),
                  iconName: 'trash-01' as const,
                  disabled: deleteDisabled,
                  onClick: onDelete,
                },
              ],
            }
          : {}),
      },
    }),
    [
      title,
      counter,
      exportOptions,
      ribbonConfig,
      canDelete,
      deleteDisabled,
      onDelete,
      t,
      st,
    ]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
